import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Snackbar,
  Tooltip
} from '@mui/material';
import {
  ContentCopy,
  People,
  Mouse,
  PersonOff,
  AttachMoney,
  TrendingUp,
  Star
} from '@mui/icons-material';
import QRCode from 'qrcode';
import { useAuth } from '../../contexts/AuthContext';
import BackButton from '../BackButton';

/**
 * Affiliate Dashboard (invite-only program).
 *
 * All data comes from the affiliate-stats Netlify function, which reads
 * the Admin-SDK-only affiliateEarnings / affiliateClicks collections —
 * the client never touches them directly. Users without granted
 * affiliate access get a 403 from the function and see the invite-only
 * message instead of a dashboard.
 */

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const statusChip = (status) => {
  switch (status) {
    case 'active':
      return <Chip label="ACTIVE" size="small" color="success" />;
    case 'trial':
      return <Chip label="TRIAL" size="small" color="warning" />;
    case 'cancelled':
      return <Chip label="CANCELLED" size="small" variant="outlined" />;
    default:
      return <Chip label="FREE" size="small" variant="outlined" />;
  }
};

const AffiliateDashboardScreen = () => {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notGranted, setNotGranted] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  const loadStats = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();

      const functionUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:8888/.netlify/functions/affiliate-stats'
        : '/.netlify/functions/affiliate-stats';

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      if (response.status === 403) {
        setNotGranted(true);
        return;
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load affiliate stats');
      }
      setStats(data);
    } catch (err) {
      console.error('Error loading affiliate stats:', err);
      setError(err.message || 'Failed to load affiliate stats');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // QR code for the referral link (same pattern as the legacy screen)
  useEffect(() => {
    const link = stats?.affiliate?.link;
    if (!link) return;
    QRCode.toDataURL(link, { width: 160, margin: 1 })
      .then(setQrCodeDataUrl)
      .catch((err) => console.error('Error generating QR code:', err));
  }, [stats]);

  const handleCopyLink = async () => {
    const link = stats?.affiliate?.link;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopySuccess(true);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      setError('Could not copy the link — copy it manually from the card above.');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading your affiliate dashboard...
        </Typography>
      </Container>
    );
  }

  if (notGranted) {
    return (
      <Container maxWidth="md" sx={{ mt: 6, mb: 6 }}>
        <BackButton />
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            The affiliate program is invite-only
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Affiliate access is granted personally by the Bettor Advantage team.
            If you've been invited, make sure you're signed in with the email
            address your invitation was issued to. Otherwise, there's nothing
            to sign up for here — access can't be requested in the app.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error || !stats) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadStats}>
              Retry
            </Button>
          }
        >
          {error || 'Affiliate stats are unavailable right now.'}
        </Alert>
      </Container>
    );
  }

  const { affiliate, kpis, earningsByMonth, subscribers, payouts, nextPayout } = stats;
  const maxMonthCents = Math.max(...earningsByMonth.map((m) => m.shareCents), 1);

  const kpiCards = [
    { label: 'LINK CLICKS', value: kpis.clicks, sub: 'all time', icon: <Mouse sx={{ fontSize: 34, color: 'primary.main' }} /> },
    { label: 'SIGNUPS', value: kpis.signups, sub: 'referred accounts', icon: <People sx={{ fontSize: 34, color: 'primary.main' }} /> },
    { label: 'ACTIVE SUBSCRIBERS', value: kpis.activeSubscribers, sub: 'paying you every month', icon: <Star sx={{ fontSize: 34, color: 'success.main' }} /> },
    { label: 'CANCELLED', value: kpis.cancelledSubscribers, sub: 'no longer accruing', icon: <PersonOff sx={{ fontSize: 34, color: 'error.main' }} /> }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <BackButton />
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2 }}>
            BETTOR ADVANTAGE
          </Typography>
          <Typography variant="h4" component="h1">
            Affiliate Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {affiliate.displayName || ''}
            {affiliate.email ? ` · ${affiliate.email}` : ''}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip label="ACTIVE" color="success" size="small" />
          <Chip label="10% REV SHARE" variant="outlined" size="small" />
        </Box>
      </Box>

      {/* Referral link card */}
      <Paper sx={{ p: 2.5, mb: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flexGrow: 1, minWidth: 260 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
            YOUR REFERRAL LINK
          </Typography>
          <Typography variant="h6" sx={{ wordBreak: 'break-all' }}>
            bettoradvantage.com/r/
            <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
              {affiliate.code}
            </Box>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Code <strong>{affiliate.code}</strong> · works on web &amp; app signups · share it anywhere
          </Typography>
        </Box>
        {qrCodeDataUrl && (
          <Box
            component="img"
            src={qrCodeDataUrl}
            alt="Referral link QR code"
            sx={{ width: 76, height: 76, borderRadius: 1, border: 1, borderColor: 'divider' }}
          />
        )}
        <Button
          variant="contained"
          color="success"
          startIcon={<ContentCopy />}
          onClick={handleCopyLink}
        >
          Copy Link
        </Button>
      </Paper>

      {/* KPI cards */}
      <Grid container spacing={3} sx={{ mb: 1 }}>
        {kpiCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                    {card.label}
                  </Typography>
                  {card.icon}
                </Box>
                <Typography variant="h4">{card.value}</Typography>
                <Typography variant="body2" color="text.secondary">{card.sub}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Earnings summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderColor: 'success.main' }} variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                EARNINGS · THIS MONTH
              </Typography>
              <Typography variant="h4" color="success.main">{money(kpis.earningsThisMonthCents)}</Typography>
              <Typography variant="body2" color="text.secondary">
                10% of {money(kpis.grossReferredCents)} referred payments (lifetime gross)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                LIFETIME EARNED
              </Typography>
              <Typography variant="h4">{money(kpis.lifetimeEarningsCents)}</Typography>
              <Typography variant="body2" color="text.secondary">
                from {kpis.everSubscribed} subscriber{kpis.everSubscribed === 1 ? '' : 's'} who have paid
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                NEXT PAYOUT
              </Typography>
              <Typography variant="h4">{money(nextPayout.shareCents)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {nextPayout.label} accrual · paid manually by the owners after month-end
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Earnings by month + funnel */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant="h6">Earnings by month</Typography>
          <Typography variant="body2" color="text.secondary">your 10% cut, by month earned</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 150, mt: 2, px: 1 }}>
          {earningsByMonth.map((month) => (
            <Box key={month.month} sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {money(month.shareCents)}
              </Typography>
              <Tooltip title={`${month.payments} payment${month.payments === 1 ? '' : 's'}`}>
                <Box
                  sx={{
                    height: Math.max(6, Math.round((month.shareCents / maxMonthCents) * 100)),
                    borderRadius: '6px 6px 0 0',
                    background: 'linear-gradient(180deg, #37b878, #1d5c3f)',
                    mt: 0.5
                  }}
                />
              </Tooltip>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {month.label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ borderTop: 1, borderColor: 'divider', mt: 2, pt: 1.5 }}>
          Funnel: <strong>{kpis.clicks}</strong> clicks → <strong>{kpis.signups}</strong> signups →{' '}
          <strong>{kpis.everSubscribed}</strong> subscribed → <strong>{kpis.activeSubscribers}</strong> still active
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
          <TrendingUp fontSize="small" /> Earnings appear the moment a referred payment clears, and stop the month a subscriber cancels.
        </Typography>
      </Paper>

      {/* Referred subscribers */}
      <Paper sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ p: 2.5, pb: 1 }}>
          Your referred subscribers
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Subscriber</strong></TableCell>
                <TableCell><strong>Joined</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Payments</strong></TableCell>
                <TableCell><strong>They've paid</strong></TableCell>
                <TableCell><strong>Your cut</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscribers.map((sub, index) => (
                <TableRow key={`${sub.maskedEmail}-${index}`}>
                  <TableCell>{sub.maskedEmail}</TableCell>
                  <TableCell>
                    {sub.joinedAt
                      ? new Date(sub.joinedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : '—'}
                  </TableCell>
                  <TableCell>{statusChip(sub.status)}</TableCell>
                  <TableCell>{sub.paymentsCount}</TableCell>
                  <TableCell>{money(sub.totalPaidCents)}</TableCell>
                  <TableCell><strong>{money(sub.yourCutCents)}</strong></TableCell>
                </TableRow>
              ))}
              {subscribers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No referred signups yet — share your link to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          Emails are masked for privacy.
        </Typography>
      </Paper>

      {/* Payout history */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Payout history
        </Typography>
        {payouts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No completed months yet. Earnings accrue during the month and are
            paid out manually by the owners after month-end.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Month</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payouts.map((payout) => (
                  <TableRow key={payout.month}>
                    <TableCell>{payout.label}</TableCell>
                    <TableCell>{money(payout.shareCents)}</TableCell>
                    <TableCell>
                      <Chip label="ACCRUED" size="small" color="warning" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AttachMoney fontSize="small" />
          You earn 10% of every payment made by people who signed up with your code, for as long as they stay subscribed.
        </Typography>
      </Paper>

      <Snackbar
        open={copySuccess}
        autoHideDuration={2500}
        onClose={() => setCopySuccess(false)}
        message="Referral link copied"
      />
    </Container>
  );
};

export default AffiliateDashboardScreen;
